import { Tooltip } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  APP_OPERATIONS,
  PERM_MOD_SETTINGS_APPS,
  User,
  VisibilityRule,
} from 'src/constants';
import { RouteContext, useFeatureFlags } from 'src/context';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import RowDivider from 'src/legacy/components/RowDivider';
import { ModuleFormValuesType } from 'src/legacy/components/Settings/AppSetupPage/SetupApp/appSetupTypes';
import { useEditingApp } from 'src/legacy/components/Settings/AppSetupPage/SetupApp/useEditingApp';
import { useContentfulApps } from 'src/legacy/components/Settings/AppSetupPage/useContentfulApps';
import { AppVisibilityForm } from 'src/legacy/components/Settings/AppVisibilityForm';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { UpgradeTooltipTitle } from 'src/legacy/components/Tooltip/UpgradeTooltipTitle';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { useGetClientVisibilityCountQuery } from 'src/services/api/clientVisibilityApi';
import { Company } from 'src/store/clients/types';
import { RootState } from 'src/store/reduxTypes';
import { updateModuleSettings } from 'src/store/settings/actions';
import {
  AllVisibilityRules,
  getClientVisibilityCount,
  getVisibilityConfig,
  getVisibilityType,
  VisibilityScenario,
  VisibilityType,
} from 'src/utils/app_visibility';
import { arrayToMap } from 'src/utils/array';
import { ensureUnreachable } from 'src/utils/common_utils';
import { getOperation } from 'src/utils/UserUtils';

import { AppVisibilityEditButton } from './AppVisibilityEditButton';
import { AppVisibilityLoadingSkeleton } from './AppVisibilityLoadingSkeleton';

type AddAnAppProps = {
  scenario: VisibilityScenario.AddAnApp;
  onVisibilityConfigSaved: (values: VisibilityRule[]) => void;
};

type EditAppProps = {
  scenario: VisibilityScenario.EditApp;
};

type Props = AddAnAppProps | EditAppProps;

const userSelector = createSelector(
  [
    (state: RootState) => state.clients.activeClients,
    (state: RootState) => state.clients.activeCompanies,
  ],
  (clients: User[] = [], companies: Company[] = []) => ({
    clients,
    companies: companies,
  }),
);

export const AppVisibilityConfigSetting = (props: Props) => {
  const { EnableFeatureAppVisibility } = useFeatureFlags();
  const { scenario } = props;
  const { t } = useTranslation();
  const [isVisibilityModalOpen, setVisibilityModalOpen] = React.useState(false);
  const editingModule = useEditingApp();
  const { query } = useContext(RouteContext);
  const { appId } = query;

  const dispatch = useAppDispatch();
  const {
    data: serverClientCount,
    refetch,
    isFetching,
  } = useGetClientVisibilityCountQuery(
    {
      id: editingModule?.id || '',
    },
    {
      skip: !editingModule?.id,
    },
  );

  const permissions = useAppSelector((state) => state.user.data?.permissions);

  const updateVisibilityOperation = getOperation(
    PERM_MOD_SETTINGS_APPS,
    APP_OPERATIONS.UpdateVisibility,
    permissions,
  );

  const disableUpdateVisibilityOperation = updateVisibilityOperation
    ? updateVisibilityOperation.planLimit === 0
    : false;

  const { internalApps } = useContentfulApps({ includeInstalledApps: true });
  const isInternalApp =
    appId && internalApps.some((app) => app.copilotAppId === appId);

  const { clients, companies } = useAppSelector(userSelector);
  const { data: { moduleSettings } = {} } = useGetInstallsQuery();

  const [localVisibilityConfig, setLocalVisibilityConfig] = useState<
    VisibilityRule[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);

  const moduleSettingsMap = React.useMemo(
    () => arrayToMap(moduleSettings ?? [], 'id'),
    [moduleSettings],
  );

  const moduleVisbilityConfig =
    moduleSettingsMap.get(editingModule?.id || '')?.visibilityConfig ?? [];

  const clientCount =
    scenario === VisibilityScenario.EditApp
      ? (serverClientCount ?? 0)
      : getClientVisibilityCount(clients, companies, localVisibilityConfig);

  if (!moduleSettings) return null;

  const visibilityType =
    scenario === VisibilityScenario.AddAnApp
      ? getVisibilityType(localVisibilityConfig)
      : getVisibilityType(moduleVisbilityConfig);

  if (!editingModule || isInternalApp) {
    return null;
  }

  async function handleVisibilityConfigSaved(values: AllVisibilityRules) {
    const newVisibilityConfig = getVisibilityConfig(values, {
      enableFeatureAppVisibility: EnableFeatureAppVisibility,
    });
    switch (scenario) {
      case VisibilityScenario.AddAnApp:
        const { onVisibilityConfigSaved } = props;
        onVisibilityConfigSaved(newVisibilityConfig);
        setLocalVisibilityConfig(newVisibilityConfig);
        break;
      case VisibilityScenario.EditApp:
        setIsSaving(true);
        try {
          await addAppVisibilityToModule(values, editingModule);
          refetch();
        } finally {
          setIsSaving(false);
        }
        break;
      default:
        ensureUnreachable(scenario);
    }
  }

  const addAppVisibilityToModule = async (
    values: AllVisibilityRules,
    currentModule: ModuleFormValuesType | null,
  ) => {
    const moduleIndex = moduleSettings.findIndex(
      (module) => module.id === currentModule?.id,
    );

    const newVisibilityConfig = getVisibilityConfig(values, {
      enableFeatureAppVisibility: EnableFeatureAppVisibility,
    });

    const updatedModules = moduleSettings.with(moduleIndex, {
      ...moduleSettings[moduleIndex],
      visibilityConfig: newVisibilityConfig,
    });
    await dispatch(updateModuleSettings(updatedModules));
  };

  return (
    <>
      <div className="mt-10">
        <div className="text-lg font-medium leading-6">
          {t(($) => $.page_apps.client_facing_app_visibility.title)}
        </div>
      </div>
      <RowDivider mb={0} />
      <div className="py-6 h-10">
        {isSaving || isFetching ? (
          <AppVisibilityLoadingSkeleton />
        ) : (
          <div className="flex flex-row justify-between items-center">
            <div>
              <BaseTypography fontType="12Medium">
                {(() => {
                  switch (visibilityType) {
                    case VisibilityType.CustomVisibility:
                      return t(
                        ($) =>
                          $.page_apps.client_facing_app_visibility.subtitle
                            .custom,
                      );
                    case VisibilityType.NoVisibility:
                      return t(
                        ($) =>
                          $.page_apps.client_facing_app_visibility.subtitle
                            .none,
                      );
                    case VisibilityType.VisibleToEveryone:
                      return t(
                        ($) =>
                          $.page_apps.client_facing_app_visibility.subtitle
                            .all_clients,
                      );
                    default:
                      return ensureUnreachable(visibilityType);
                  }
                })()}
              </BaseTypography>
              <div className="mt-2">
                {moduleSettings.length > 0 && (
                  <BaseTypography
                    className="text-[#6B6F76]"
                    fontType="12Regular"
                  >
                    {t(($) => $.page_apps.client_facing_app_visibility.count, {
                      count: clientCount,
                    })}
                  </BaseTypography>
                )}
              </div>
            </div>
            <Tooltip
              title={
                disableUpdateVisibilityOperation ? (
                  <UpgradeTooltipTitle
                    description={'To customize App Visibility Settings'}
                  />
                ) : (
                  ''
                )
              }
            >
              <div>
                <AppVisibilityEditButton
                  onClick={() => setVisibilityModalOpen(true)}
                  disabled={disableUpdateVisibilityOperation}
                >
                  <BaseTypography fontType="13Medium">Edit</BaseTypography>
                </AppVisibilityEditButton>
              </div>
            </Tooltip>
          </div>
        )}
        {isVisibilityModalOpen && (
          <AppVisibilityForm
            moduleVisbilityConfig={
              scenario === VisibilityScenario.EditApp
                ? moduleVisbilityConfig
                : localVisibilityConfig
            }
            onVisibilityConfigSaved={handleVisibilityConfigSaved}
            moduleSettings={moduleSettings}
            onCloseModal={() => setVisibilityModalOpen(false)}
            open={isVisibilityModalOpen}
          />
        )}
      </div>
      <RowDivider mt={1} />
    </>
  );
};
